/**
 * Drop-in replacement for react-i18next's useTranslation.
 * Re-normalizes every t() result so plain objects never reach React as text children,
 * even if a code path bypasses the wrapped i18n.t (HMR, future library changes).
 */
import { useTranslation as useTranslationOriginal } from "react-i18next";
import { useCallback } from "react";
import {
  normalizeTranslationResultForDisplay,
  extractReturnObjects,
} from "@/i18n";

function extractOptsFromTArgs(args: unknown[]): unknown {
  if (args.length >= 2) {
    const second = args[1];
    if (second !== null && typeof second === "object" && !Array.isArray(second)) return second;
  }
  if (args.length >= 3) {
    const third = args[2];
    if (third !== null && typeof third === "object" && !Array.isArray(third)) return third;
  }
  return undefined;
}

export function useTranslation(
  ...args: Parameters<typeof useTranslationOriginal>
): ReturnType<typeof useTranslationOriginal> {
  const result = useTranslationOriginal(...args);
  const rawT = result.t;

  const t = useCallback(
    ((...callArgs: Parameters<typeof rawT>) => {
      try {
        const out = rawT(...callArgs);
        const opts = extractOptsFromTArgs(callArgs as unknown[]);
        return normalizeTranslationResultForDisplay(out, extractReturnObjects(opts));
      } catch (e) {
        console.error("[i18n] useSafeTranslation t() failed:", e);
        return "";
      }
    }) as typeof rawT,
    [rawT]
  );

  return { ...result, t };
}
